import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  Spinner,
  Alert,
  Chip,
  Button,
  TextField,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type FaqItem,
  type FaqVisibility,
  type CreateFaqInput,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';

/**
 * FAQ management (faq:edit) — CRUD over the FAQs the public Help/FAQ pages
 * render. Create/edit happens in an inline panel; reorder via `displayOrder`,
 * show/hide via the visibility toggle. EN + ES. Every change is audited server-side.
 */

interface Draft {
  id?: string;
  topic: string;
  question: string;
  answer: string;
  questionEs: string;
  answerEs: string;
  displayOrder: string;
  isDisplayed: boolean;
}

const EMPTY_DRAFT: Draft = {
  topic: '',
  question: '',
  answer: '',
  questionEs: '',
  answerEs: '',
  displayOrder: '',
  isDisplayed: true,
};

const toDraft = (f: FaqItem): Draft => ({
  id: f.id,
  topic: f.topic,
  question: f.question,
  answer: f.answer,
  questionEs: f.questionEs ?? '',
  answerEs: f.answerEs ?? '',
  displayOrder: f.displayOrder != null ? String(f.displayOrder) : '',
  isDisplayed: f.isDisplayed,
});

// Non-empty sentinel for the "All topics" option. An empty-string value leaves
// the DSL Select's floating label un-shrunk (it overlaps the value text), so we
// use a reserved value and map it back to "no filter".
const ALL_TOPICS = '__all__';

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function FaqManagement() {
  const caps = useCapabilities();
  const canView = caps.has('faq:read');
  const canEdit = caps.has('faq:edit');

  const [items, setItems] = useState<FaqItem[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [topicFilter, setTopicFilter] = useState(ALL_TOPICS);
  const [visibility, setVisibility] = useState<FaqVisibility>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listFaqs({
        topic: topicFilter === ALL_TOPICS ? undefined : topicFilter,
        visibility,
      });
      setItems(res.items);
      setTopics(res.topics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, [topicFilter, visibility]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload: CreateFaqInput = {
        topic: draft.topic.trim(),
        question: draft.question.trim(),
        answer: draft.answer.trim(),
        questionEs: draft.questionEs.trim() || null,
        answerEs: draft.answerEs.trim() || null,
        displayOrder: draft.displayOrder ? Number(draft.displayOrder) : undefined,
        isDisplayed: draft.isDisplayed,
      };
      if (draft.id) await adminApiClient.updateFaq(draft.id, payload);
      else await adminApiClient.createFaq(payload);
      setFlash({ kind: 'success', msg: draft.id ? 'FAQ updated.' : 'FAQ created.' });
      setDraft(null);
      void load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const toggleDisplayed = async (f: FaqItem) => {
    try {
      await adminApiClient.updateFaq(f.id, { isDisplayed: !f.isDisplayed });
      void load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'Update failed' });
    }
  };

  const remove = async (f: FaqItem) => {
    if (!window.confirm(`Delete this FAQ?\n\n${f.question}`)) return;
    try {
      await adminApiClient.deleteFaq(f.id);
      setFlash({ kind: 'success', msg: 'FAQ deleted.' });
      void load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'Delete failed' });
    }
  };

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view FAQs.</Alert>;

  const canSave =
    !!draft && !!draft.topic.trim() && !!draft.question.trim() && !!draft.answer.trim() && !saving;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          FAQ management
        </Typography>
        {canEdit && !draft && (
          <Button
            ariaLabel="New FAQ"
            buttonText="New FAQ"
            variant="contained"
            onClick={() => setDraft({ ...EMPTY_DRAFT })}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        The questions shown on the public Help / FAQ pages.
      </Typography>

      {!canEdit && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      {canEdit && draft && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            {draft.id ? 'Edit FAQ' : 'New FAQ'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="Topic"
              ariaLabel="Topic"
              value={draft.topic}
              onChange={(e) => setDraft({ ...draft, topic: e.target.value })}
              disabled={saving}
              fullWidth
              multiline={false}
            />
            <TextField
              label="Question (EN)"
              ariaLabel="Question English"
              value={draft.question}
              onChange={(e) => setDraft({ ...draft, question: e.target.value })}
              disabled={saving}
              fullWidth
              multiline={false}
            />
            <TextField
              label="Answer (EN)"
              ariaLabel="Answer English"
              value={draft.answer}
              onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
              disabled={saving}
              fullWidth
              multiline
            />
            <TextField
              label="Question (ES) — optional"
              ariaLabel="Question Spanish"
              value={draft.questionEs}
              onChange={(e) => setDraft({ ...draft, questionEs: e.target.value })}
              disabled={saving}
              fullWidth
              multiline={false}
            />
            <TextField
              label="Answer (ES) — optional"
              ariaLabel="Answer Spanish"
              value={draft.answerEs}
              onChange={(e) => setDraft({ ...draft, answerEs: e.target.value })}
              disabled={saving}
              fullWidth
              multiline
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Display order"
                ariaLabel="Display order"
                type="number"
                value={draft.displayOrder}
                onChange={(e) => setDraft({ ...draft, displayOrder: e.target.value })}
                disabled={saving}
                multiline={false}
                sx={{ width: 160 }}
              />
              <Select
                ariaLabel="Visibility"
                label="Visibility"
                native
                size="small"
                sx={{ width: 160 }}
                value={draft.isDisplayed ? 'shown' : 'hidden'}
                onChange={(_e, v) => setDraft({ ...draft, isDisplayed: v === 'shown' })}
                options={[
                  { value: 'shown', label: 'Shown' },
                  { value: 'hidden', label: 'Hidden' },
                ]}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                ariaLabel="Cancel"
                buttonText="Cancel"
                variant="outlined"
                sx={NEUTRAL_BTN}
                onClick={() => setDraft(null)}
                disabled={saving}
              />
              <Button
                ariaLabel="Save FAQ"
                buttonText={saving ? 'Saving…' : 'Save'}
                variant="contained"
                onClick={save}
                disabled={!canSave}
              />
            </Box>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter by topic"
          label="Topic"
          native
          size="small"
          sx={{ width: 200 }}
          value={topicFilter}
          onChange={(_e, v) => setTopicFilter(v as string)}
          options={[{ value: ALL_TOPICS, label: 'All topics' }, ...topics.map((t) => ({ value: t, label: t }))]}
        />
        <Select
          ariaLabel="Filter by visibility"
          label="Visibility"
          native
          size="small"
          sx={{ width: 160 }}
          value={visibility}
          onChange={(_e, v) => setVisibility(v as FaqVisibility)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'shown', label: 'Shown' },
            { value: 'hidden', label: 'Hidden' },
          ]}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : items.length === 0 ? (
        <EmptyState icon="menuBook" title="No FAQs" body="No FAQs match this filter." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((f) => (
            <Paper key={f.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={f.topic} size="small" color="primary" />
                    {!f.isDisplayed && <Chip label="Hidden" size="small" />}
                    {f.answerEs && <Chip label="ES" size="small" />}
                    {f.displayOrder != null && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        #{f.displayOrder}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {f.question}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {f.answer}
                  </Typography>
                </Box>
                {canEdit && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                      ariaLabel={f.isDisplayed ? 'Hide FAQ' : 'Show FAQ'}
                      buttonText={f.isDisplayed ? 'Hide' : 'Show'}
                      variant="outlined"
                      size="small"
                      sx={NEUTRAL_BTN}
                      onClick={() => toggleDisplayed(f)}
                    />
                    <Button
                      ariaLabel="Edit FAQ"
                      buttonText="Edit"
                      variant="outlined"
                      size="small"
                      sx={NEUTRAL_BTN}
                      onClick={() => setDraft(toDraft(f))}
                    />
                    <Button
                      ariaLabel="Delete FAQ"
                      buttonText="Delete"
                      variant="outlined"
                      size="small"
                      sx={DANGER_BTN}
                      onClick={() => remove(f)}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
